<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <FlexCol gap-4 class="seller-home min-h-0 pb-6">
    <TheLoader v-if="loading" />

    <PageHeader
      :title="title"
      :subtitle="subtitle"
      :breadcrumb="false">
      <template #actions>
        <div v-if="includeSellerFilter" class="w-[320px] max-w-full">
          <InputZoom
            v-model="selectedSellerId"
            v-model:completeObject="selectedSeller"
            database="crm_seller_user"
            item-text="sellerName"
            item-value="systemuserid"
            :item-autocomplete="['sellerName', 'sellerEmail', 'loginEmail']"
            placeholder="Selecione a vendedora" />
        </div>

        <div
          v-if="dashboard.linkedSeller?.name"
          class="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600">
          <span class="font-semibold text-slate-800">{{ dashboard.linkedSeller.name }}</span>
          <span class="ml-2">{{ linkedSellerCaption }}</span>
        </div>

        <InputMonth v-model="selectedMonthRange" pill-mode pill-label="Mes:" />
      </template>
    </PageHeader>

    <div v-if="dashboard.warnings.length > 0" class="space-y-3">
      <div
        v-for="warning in dashboard.warnings"
        :key="warning"
        class="rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        {{ warning }}
      </div>
    </div>

    <div class="grid gap-4 md:grid-cols-3">
      <Card noSpace class="rounded-[16px] border border-slate-200">
        <div class="px-5 py-4">
          <div class="text-sm text-slate-500">Pedidos com parcelas vencendo no mes</div>
          <div class="mt-1 text-[30px] font-semibold tracking-[-0.5px] text-[#13161b]">{{ dashboard.summary.orders }}</div>
        </div>
      </Card>

      <Card noSpace class="rounded-[16px] border border-slate-200">
        <div class="px-5 py-4">
          <div class="text-sm text-slate-500">Comissao estimada</div>
          <div class="mt-1 text-[30px] font-semibold tracking-[-0.5px] text-[#13161b]">{{ formatMoney(dashboard.summary.estimatedCommission) }}</div>
        </div>
      </Card>

      <Card noSpace class="rounded-[16px] border border-slate-200">
        <div class="px-5 py-4">
          <div class="text-sm text-slate-500">Comissao a ser recebida</div>
          <div class="mt-1 text-[30px] font-semibold tracking-[-0.5px] text-[#13161b]">{{ formatMoney(dashboard.summary.commission) }}</div>
        </div>
      </Card>
    </div>

    <div v-if="!loading && dashboard.groups.length === 0" class="rounded-[16px] border border-dashed border-slate-300 bg-white px-5 py-8 text-sm text-slate-500">
      {{ emptyStateMessage }}
    </div>

    <div v-else class="space-y-4">
      <Card v-for="group in dashboard.groups" :key="group.createdMonth" noSpace class="overflow-hidden rounded-[16px] border border-slate-200">
        <button
          type="button"
          class="w-full border-b border-slate-200 px-5 py-4 text-left"
          @click="toggleGroup(group.createdMonth)">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div class="flex items-center gap-3">
                <div>
                  <div class="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Mes de insercao</div>
                  <h2 class="mt-1 text-[24px] font-semibold tracking-[-0.4px] text-[#13161b]">{{ group.createdMonthLabel }}</h2>
                </div>

                <span class="mt-5 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500">
                  <i class="fa-solid transition-transform" :class="isGroupExpanded(group.createdMonth) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </span>
              </div>

              <p class="mt-1 text-sm text-slate-500">{{ group.totalOrders }} pedido(s) com parcelas vencendo em {{ dashboard.monthLabel.toLowerCase() }}</p>
            </div>

            <div class="grid gap-3 sm:grid-cols-3">
              <div class="rounded-[12px] bg-slate-50 px-4 py-3">
                <div class="text-xs uppercase tracking-[0.08em] text-slate-400">Pedidos</div>
                <div class="mt-1 text-lg font-semibold text-[#13161b]">{{ group.totalOrders }}</div>
              </div>

              <div class="rounded-[12px] bg-slate-50 px-4 py-3">
                <div class="text-xs uppercase tracking-[0.08em] text-slate-400">Comissao estimada</div>
                <div class="mt-1 text-lg font-semibold text-[#13161b]">{{ formatMoney(group.totalCommission) }}</div>
              </div>

              <div class="rounded-[12px] bg-slate-50 px-4 py-3">
                <div class="text-xs uppercase tracking-[0.08em] text-slate-400">Comissao recebida</div>
                <div class="mt-1 text-lg font-semibold text-[#13161b]">{{ formatMoney(group.receivedCommission) }}</div>
              </div>
            </div>
          </div>
        </button>

        <div v-if="isGroupExpanded(group.createdMonth) && group.goal" class="border-b border-slate-200 px-5 py-4">
          <div class="rounded-[12px] border border-slate-200 bg-slate-50 px-4 py-3">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div class="text-sm font-semibold text-[#13161b]">{{ group.goal.title }}</div>
                <div class="mt-1 text-sm text-slate-500">
                  {{ formatMoney(group.goal.actualMoney) }} de {{ formatMoney(group.goal.targetMoney) }}
                </div>
              </div>

              <div class="flex items-center gap-3">
                <span
                  class="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                  :class="group.goal.achieved ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'">
                  {{ group.goal.achieved ? 'Meta atendida' : 'Meta nao atendida' }}
                </span>

                <div class="text-right">
                  <div class="text-xs uppercase tracking-[0.08em] text-slate-400">Atingimento</div>
                  <div class="text-lg font-semibold text-[#13161b]">{{ formatPercentage(group.goal.percentage) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isGroupExpanded(group.createdMonth) && group.warnings?.length" class="space-y-2 border-b border-slate-200 px-5 py-4">
          <div
            v-for="warning in group.warnings"
            :key="warning"
            class="rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {{ warning }}
          </div>
        </div>

        <div v-if="isGroupExpanded(group.createdMonth)" class="overflow-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-slate-50 text-left text-slate-500">
              <tr>
                <th class="px-5 py-3 font-medium text-center">Pago</th>
                <th class="px-5 py-3 font-medium">Pedido</th>
                <th class="px-5 py-3 font-medium">Classificacao</th>
                <th class="px-5 py-3 font-medium">Data do pedido</th>
                <th class="px-5 py-3 font-medium">Ultimo vencimento</th>
                <th class="px-5 py-3 font-medium text-right">Valor do pedido</th>
                <th class="px-5 py-3 font-medium text-right">Valor rentabilidade</th>
                <th class="px-5 py-3 font-medium">Numero da parcela</th>
                <th class="px-5 py-3 font-medium text-right">Valor da parcela</th>
                <th class="px-5 py-3 font-medium text-right">Base de calculo</th>
                <th class="px-5 py-3 font-medium text-right">Comissao</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="order in group.orders" :key="order.salesorderId" class="border-t border-slate-200 align-top">
                <td class="px-5 py-4 text-center">
                  <span
                    class="inline-flex h-8 w-8 items-center justify-center rounded-full"
                    :class="order.hasPaidInstallment ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-300'">
                    <i class="fa-solid fa-check"></i>
                  </span>
                </td>

                <td class="px-5 py-4">
                  <div class="font-medium text-[#13161b]">{{ order.clientName || '-' }}</div>
                  <div class="mt-1 text-xs text-slate-500">
                    {{ order.orderNumber || '-' }} · {{ order.quoteNumber || '-' }}
                  </div>
                  <div v-if="order.notes?.length" class="mt-2 flex flex-wrap gap-2">
                    <span v-for="note in order.notes" :key="note" class="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[11px] text-slate-600">
                      {{ note }}
                    </span>
                  </div>
                </td>

                <td class="px-5 py-4 text-slate-600">
                  <span
                    class="inline-flex rounded-full px-3 py-1 text-xs font-semibold"
                    :class="getOrderClassificationClass(order)">
                    {{ getOrderClassificationLabel(order) }}
                  </span>
                </td>

                <td class="px-5 py-4 text-slate-600">
                  {{ formatDate(order.createdOn) || '-' }}
                </td>

                <td class="px-5 py-4 text-slate-600">
                  {{ formatDate(order.paidAt) || '-' }}
                </td>

                <td class="px-5 py-4 text-right font-medium text-[#13161b]">
                  {{ formatMoney(order.totalAmount) }}
                </td>

                <td class="px-5 py-4 text-right font-medium text-[#13161b]">
                  {{ formatMoney(order.profitabilityAmount) }}
                </td>

                <td class="px-5 py-4 text-slate-600">
                  {{ formatInstallmentNumbers(order) }}
                </td>

                <td class="px-5 py-4 text-right font-medium text-[#13161b]">
                  {{ formatMoney(order.installmentAmount) }}
                </td>

                <td class="px-5 py-4 text-right text-slate-600">
                  <div>{{ formatMoney(order.baseAmount) }}</div>
                </td>

                <td class="px-5 py-4 text-right">
                  <div class="font-semibold text-[#13161b]">{{ formatMoney(order.commissionAmount) }}</div>
                  <div v-if="order.commissionPercentages?.length" class="mt-1 text-[11px] text-slate-400">
                    {{ order.commissionPercentages.join(', ') }}%
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  </FlexCol>
</template>

<script setup>
import { defineProps, onMounted, ref, watch } from 'vue'
import { get } from '@/crud'
import { useFormat } from '@/composables/useFormat'

const props = defineProps({
  api: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: 'Recebimentos',
  },
  subtitle: {
    type: String,
    default: 'Pedidos com parcelas vencendo no mes de referencia, agrupados pelo mes em que foram inseridos.',
  },
  linkedSellerCaption: {
    type: String,
    default: 'vinculada ao seu usuario',
  },
  includeSellerFilter: {
    type: Boolean,
    default: false,
  },
  emptyStateMessage: {
    type: String,
    default: 'Nenhum pedido com parcelas vencendo foi encontrado no mes selecionado.',
  },
})

const { formatDate, formatMoneyS } = useFormat()

const loading = ref(false)
const initialized = ref(false)
const selectedSellerId = ref(null)
const selectedSeller = ref(null)
const selectedMonth = ref(currentYearMonth())
const selectedMonthRange = ref(getMonthRangeValue(selectedMonth.value))
const expandedGroups = ref({})
const dashboard = ref(buildEmptyDashboard())

watch(selectedMonthRange, async (range) => {
  if (!Array.isArray(range) || range.length < 2 || !range[0]) return

  const startDate = new Date(range[0])
  if (Number.isNaN(startDate.getTime())) return

  const nextMonth = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`
  if (nextMonth === selectedMonth.value) return

  selectedMonth.value = nextMonth

  if (props.includeSellerFilter && !initialized.value) return
  await loadReceipts()
})

watch(selectedSellerId, async (nextSellerId, previousSellerId) => {
  if (!props.includeSellerFilter) return
  if (!initialized.value) return
  if (nextSellerId === previousSellerId) return
  await loadReceipts()
})

onMounted(async () => {
  await loadReceipts()
  initialized.value = true
})

async function loadReceipts() {
  loading.value = true

  try {
    const response = await get(props.api, {
      params: {
        month: selectedMonth.value,
        sellerId: props.includeSellerFilter ? selectedSellerId.value || undefined : undefined,
      },
    })

    if (response?.error) {
      throw new Error(response?.data?.[0]?.message || 'Erro ao carregar recebimentos.')
    }

    dashboard.value = {
      linkedSeller: response?.linkedSeller || null,
      month: response?.month || selectedMonth.value,
      monthLabel: response?.monthLabel || '',
      summary: {
        orders: Number(response?.summary?.orders || 0),
        commission: Number(response?.summary?.commission || 0),
        estimatedCommission: Number(response?.summary?.estimatedCommission || 0),
        value: Number(response?.summary?.value || 0),
      },
      groups: Array.isArray(response?.groups) ? response.groups : [],
      warnings: Array.isArray(response?.warnings) ? response.warnings : [],
    }

    syncExpandedGroups()
  } catch (error) {
    dashboard.value = buildEmptyDashboard({
      month: selectedMonth.value,
      warnings: [error.message || 'Erro ao carregar recebimentos.'],
    })
    expandedGroups.value = {}
  } finally {
    loading.value = false
  }
}

function buildEmptyDashboard(overrides = {}) {
  return {
    linkedSeller: null,
    month: selectedMonth.value,
    monthLabel: '',
    summary: {
      orders: 0,
      commission: 0,
      estimatedCommission: 0,
      value: 0,
    },
    groups: [],
    warnings: [],
    ...overrides,
  }
}

function formatMoney(value) {
  return formatMoneyS(String(Number(value || 0)))
}

function formatPercentage(value) {
  return `${Number(value || 0).toFixed(2)}%`
}

function getOrderClassificationLabel(order) {
  if (order?.proposalClassificationName) return order.proposalClassificationName
  if (order?.saleType === 'new_sale') return 'Nova venda'
  if (order?.saleType === 'renewal') return 'Renovacao'
  if (order?.saleType === 'cross_sell') return 'Adicao'
  return 'Nao classificado'
}

function getOrderClassificationClass(order) {
  if (order?.saleType === 'new_sale') {
    return 'bg-emerald-100 text-emerald-700'
  }

  if (order?.saleType === 'renewal') {
    return 'bg-sky-100 text-sky-700'
  }

  if (order?.saleType === 'cross_sell') {
    return 'bg-amber-100 text-amber-700'
  }

  return 'bg-slate-200 text-slate-700'
}

function formatInstallmentNumbers(order) {
  const total = Number(order?.totalInstallmentsCount || 0)
  const numbers = String(order?.installmentNumbers || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

  if (!numbers.length) return '-'
  if (!total) return numbers.join(', ')

  return numbers.map((item) => `${item}/${total}`).join(', ')
}

function syncExpandedGroups() {
  const nextState = {}

  dashboard.value.groups.forEach((group, index) => {
    nextState[group.createdMonth] = expandedGroups.value[group.createdMonth] ?? index === 0
  })

  expandedGroups.value = nextState
}

function toggleGroup(createdMonth) {
  expandedGroups.value[createdMonth] = !expandedGroups.value[createdMonth]
}

function isGroupExpanded(createdMonth) {
  return !!expandedGroups.value[createdMonth]
}

function currentYearMonth() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function getMonthRangeValue(yearMonth) {
  const [year, month] = String(yearMonth || '').split('-').map(Number)
  const startDate = new Date(year, (month || 1) - 1, 1)
  const endDate = new Date(year, month || 1, 0)

  startDate.setHours(0, 0, 0, 0)
  endDate.setHours(23, 59, 59, 999)

  const pad = (value) => String(value).padStart(2, '0')
  const toSqlDateTime = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`

  return [toSqlDateTime(startDate), toSqlDateTime(endDate)]
}
</script>
